import React, { forwardRef } from 'react';

const badgeBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 'var(--radius-pill)',
    border: '1px solid transparent',
    padding: '0.125rem 0.625rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    lineHeight: '1.25',
    fontFamily: 'var(--font-ui)',
    transition: 'colors 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box'
};

const variants = {
    default: {
        backgroundColor: 'var(--surface-sunken)',
        color: 'var(--text)',
        borderColor: 'var(--border)'
    },
    secondary: {
        backgroundColor: 'var(--surface-sunken)',
        color: 'var(--text-muted)',
        borderColor: 'var(--border)'
    },
    success: {
        backgroundColor: 'var(--success-soft)',
        color: 'var(--success)',
        borderColor: 'var(--success-border)'
    },
    warning: {
        backgroundColor: 'var(--warning-soft)',
        color: 'var(--warning)',
        borderColor: 'var(--warning-border)'
    },
    danger: {
        backgroundColor: 'var(--danger-soft)',
        color: 'var(--danger)',
        borderColor: 'var(--danger-border)'
    },
    destructive: {
        backgroundColor: 'var(--danger-soft)',
        color: 'var(--danger)',
        borderColor: 'var(--danger-border)'
    },
    info: {
        backgroundColor: 'var(--info-soft)',
        color: 'var(--info)',
        borderColor: 'var(--info-border)'
    },
    accent: {
        backgroundColor: 'var(--accent-soft)',
        color: 'var(--accent-ink)',
        borderColor: 'var(--accent-border)'
    },
    outline: {
        backgroundColor: 'transparent',
        color: 'var(--text)',
        borderColor: 'var(--border-strong)'
    }
};

const sizes = {
    sm: { padding: '0.0625rem 0.5rem', fontSize: '0.6875rem' },
    md: { padding: '0.1875rem 0.625rem', fontSize: '0.75rem' }
};

const Badge = forwardRef(({
    className = '',
    variant = 'default',
    size = 'md',
    style,
    children,
    ...props
}, ref) => {
    let resolvedVariant = variant;

    if (className) {
        if (className.includes('green') || className.includes('emerald')) resolvedVariant = 'success';
        else if (className.includes('blue') || className.includes('indigo') || className.includes('sky')) resolvedVariant = 'info';
        else if (className.includes('orange') || className.includes('amber') || className.includes('yellow')) resolvedVariant = 'warning';
        else if (className.includes('red') || className.includes('rose')) resolvedVariant = 'danger';
        else if (className.includes('purple') || className.includes('violet') || className.includes('gold')) resolvedVariant = 'accent';
        else if (className.includes('gray') || className.includes('slate') || className.includes('zinc')) resolvedVariant = 'secondary';
    }

    const activeStyle = variants[resolvedVariant] || variants.default;
    const sizeStyle = sizes[size] || sizes.md;

    return (
        <span
            ref={ref}
            data-variant={resolvedVariant}
            data-size={size}
            className={className}
            style={{ ...badgeBaseStyle, ...activeStyle, ...sizeStyle, ...style }}
            {...props}
        >
            {children}
        </span>
    );
});

Badge.displayName = 'Badge';

export { Badge, badgeBaseStyle as badgeStyle, variants as badgeVariants };
export default Badge;
